import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FileDropZone from "components/FileDropZone";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useDispatch, useSelector } from "react-redux";
import addURLPrefix from "utils/addURLPrefix";
import {
  createCategoryThunk,
  updateCategoryThunk,
} from "store/reducers/product/categorySlice";
import * as yup from "yup";
import SimpleBarScroll from "components/SimpleBar";

const validation = yup.object({
  name: yup.string("Enter name").required("Name required"),
  desc: yup.string("Enter desc"),
  validation_required: yup
    .boolean("Validation required")
    .required("Field is mandatory"),
  image: yup.mixed("Add an image").nullable(),
});
const CategoryCreate = ({ category = null, fullGrid = false }) => {
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      name: category?.name ?? "",
      desc: category?.desc ?? "",
      validation_required: category?.validation_required ?? false,
      image: null,
    },
    validationSchema: validation,
    onSubmit: (data) => {
      const newData = { ...data };
      if (category?.image && !clearImage) {
        delete newData["image"];
      }
      console.log(newData);
      if (category) {
        dispatch(updateCategoryThunk({ id: category.id, data: newData }));
      } else {
        dispatch(createCategoryThunk(newData));
      }
    },
    validateOnChange: false,
  });
  const [files, setFiles] = useState([]);
  const { loading, error, success, errorMessage } = useSelector(
    (state) => state.category
  );
  const [clearImage, setClearImage] = useState(false);

  useEffect(() => {
    formik.setFieldValue("image", files[0] ?? null);
  }, [files]);
  const isFullWidth = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [backDropImage, setBackDropImage] = React.useState(null);
  const [openBackDrop, setOpenBackDrop] = React.useState(false);

  const onImageClick = (image) => {
    setBackDropImage(image);
    setOpenBackDrop(true);
  };
  const handleBackdropClose = () => {
    setOpenBackDrop(false);
  };

  useEffect(() => {
    if (errorMessage?.create) {
      Object.keys(errorMessage?.create).forEach((key) => {
        console.log(key, errorMessage?.create[key][0]);
        formik.setFieldError(key, errorMessage?.create[key][0]);
      });
    }
  }, [errorMessage]);

  useEffect(() => {
    if (errorMessage?.update?.id) {
      Object.keys(errorMessage?.update?.id).forEach((key) => {
        console.log(key, errorMessage?.update?.id[key][0]);
        formik.setFieldError(key, errorMessage?.update?.id[key][0]);
      });
    }
  }, [errorMessage]);

  return (
    <>
      <SimpleBarScroll>
        <form onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={2} rowSpacing={0}>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Name"
                variant="outlined"
                name="name"
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.name}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.name)}
                helperText={formik.errors.name}
                autoComplete="new-password"
                required
              />
            </Grid>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Description"
                variant="outlined"
                name="desc"
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.desc}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.desc)}
                helperText={formik.errors.desc}
                autoComplete="new-password"
                required
              />
            </Grid>

            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Validation required"
                variant="outlined"
                name="validation_required"
                select
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.validation_required}
                onChange={formik.handleChange("validation_required")}
                error={Boolean(formik.errors.validation_required)}
                helperText={formik.errors.validation_required}
                autoComplete="new-password"
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <Box>
                <Typography variant="h6" color="gray" marginTop={2}>
                  Choose a photo
                </Typography>
                {category?.image && !clearImage ? (
                  <>
                    <Typography marginY={2} color="text.secondary">
                      Category Image
                    </Typography>
                    <Paper
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 0.5,
                        position: "relative",
                        marginLeft: 1,
                      }}
                    >
                      <LazyLoadImage
                        alt="machine image"
                        src={addURLPrefix(category?.image)}
                        height={200}
                        width={200}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                        onClick={() =>
                          onImageClick(addURLPrefix(category?.image))
                        }
                      />
                    </Paper>
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setClearImage(true)}
                        sx={{ margin: 1 }}
                        fullWidth={false}
                      >
                        Remove image
                      </Button>
                    </Box>
                  </>
                ) : (
                  <FileDropZone
                    maxFiles={1}
                    accept={{
                      "image/png": [".png"],
                      "image/jpeg": [".jpg", ".jpeg"],
                    }}
                    mode="image"
                    files={files}
                    setFiles={setFiles}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          {((!category && error?.create) ||
            (category && error?.update === category.id)) && (
            <Alert sx={{ my: 1 }} severity="error">
              Error occurred
            </Alert>
          )}

          {((!category && success?.create) ||
            (category && success?.update === category.id)) && (
            <Alert sx={{ my: 1 }} severity="success">
              Success
            </Alert>
          )}
          <LoadingButton
            loading={
              (!category && loading?.create) ||
              (category && loading?.update === category.id)
            }
            variant="contained"
            fullWidth={isFullWidth}
            type="submit"
            sx={{ paddingX: 10, marginTop: 1 }}
          >
            {category ? "Update" : "Create"}
          </LoadingButton>
        </form>
      </SimpleBarScroll>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
        onClick={handleBackdropClose}
      >
        <LazyLoadImage
          alt="machine image"
          src={backDropImage}
          height="95%"
          width="95%"
          style={{ objectFit: "contain" }}
        />
      </Backdrop>
    </>
  );
};

export default CategoryCreate;
